import * as vp from 'vopay';
import { withStyles, TextField, MultiSelect, Select } from 'vopay';
import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ReportsPage.module.scss';
import { useClientData } from '@/providers/ClientData.provider';
import { Button } from 'UI';

const classes = withStyles(styles);

type SubscriptionListSearchParams = {
  paymentMethods: string[];
  reportType: string;
  frequency: string;
  searchTerm: string;
};

type SearchFormProps = {
  paymentMethods: string;
  reportType: string;
  frequency: string;
  searchTerm: string;
  onSearch: (searchParams: SubscriptionListSearchParams) => void;
};

type FC = FunctionComponent<SearchFormProps>;

const SubscriptionListSearchForm: FC = ({
  paymentMethods,
  reportType,
  frequency,
  searchTerm,
  onSearch,
}: SearchFormProps) => {
  const {
    response: { Country },
  } = useClientData();
  const { t } = useTranslation('translations', { keyPrefix: 'Reports.Form' });

  const [currentPaymentMethods, setCurrentPaymentMethods] = useState<string[]>(
    paymentMethods ? paymentMethods.split(',') : [],
  );
  const [currentReportType, setCurrentReportType] = useState<string>(reportType);
  const [currentFrequency, setCurrentFrequency] = useState<string>(frequency);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>(searchTerm);
  const paymentMethodOptions =
    Country === 'US'
      ? [
          { value: '', label: t('SelectOptions.PaymentMethodOptions.all') },
          { value: 'Reversal', label: t('SelectOptions.PaymentMethodOptions.reversal') },
          { value: 'Credit Card', label: t('SelectOptions.PaymentMethodOptions.creditCard') },
          { value: 'Google Pay', label: t('typeOptions.googlePay') },
          { value: 'PayPal Withdrawal', label: t('typeOptions.payPalWithdrawal') },
          { value: 'Venmo Withdrawal', label: t('typeOptions.venmoWithdrawal') },
          {
            value: 'Zelle Withdrawal',
            label: t('SelectOptions.PaymentMethodOptions.zelleWithdrawal', { defaultValue: 'Zelle Withdrawal' }),
          },
          { value: 'Refund', label: t('SelectOptions.PaymentMethodOptions.refund') },
          { value: 'ACH Funding', label: t('SelectOptions.PaymentMethodOptions.achFunding') },
          { value: 'ACH Withdrawal', label: t('SelectOptions.PaymentMethodOptions.achWithdrawal') },
          { value: 'RTP Withdrawal', label: t('SelectOptions.PaymentMethodOptions.rtpWithdrawal') },
          { value: 'OCT', label: t('SelectOptions.PaymentMethodOptions.oct') },
          { value: 'Fedwire Funding', label: t('SelectOptions.PaymentMethodOptions.fedwireFunding') },
          { value: 'Fedwire Withdrawal', label: t('SelectOptions.PaymentMethodOptions.fedwireWithdrawal') },
        ]
      : [
          { value: '', label: t('SelectOptions.PaymentMethodOptions.all') },
          { value: 'EFT Withdrawal', label: t('SelectOptions.PaymentMethodOptions.eftWithdrawal') },
          { value: 'EFT Funding', label: t('SelectOptions.PaymentMethodOptions.eftFunding') },
          { value: 'VoPayInstant Withdraw', label: t('SelectOptions.PaymentMethodOptions.vopayInstantWithdraw') },
          { value: 'Bill Pay', label: t('SelectOptions.PaymentMethodOptions.billPay') },
          { value: 'Refund', label: t('SelectOptions.PaymentMethodOptions.refund') },
          { value: 'Currency Conversion', label: t('SelectOptions.PaymentMethodOptions.currencyConversion') },
          {
            value: 'Interac Money Request',
            label: t('SelectOptions.PaymentMethodOptions.interacMoneyRequest'),
            template: <vp.InteracWordmark />,
          },
          { value: 'Interac Bulk Payout', label: t('SelectOptions.PaymentMethodOptions.interacBulkPayout') },
          { value: 'Inbound e-Transfer', label: t('SelectOptions.PaymentMethodOptions.inboundETransfer') },
          { value: 'Visa Direct Push', label: t('SelectOptions.PaymentMethodOptions.visaDirectPush') },
          { value: 'Credit Card', label: t('SelectOptions.PaymentMethodOptions.creditCard') },
          { value: 'Google Pay', label: t('typeOptions.googlePay') },
          { value: 'PayPal Withdrawal', label: t('typeOptions.payPalWithdrawal') },
          { value: 'Account Transfer', label: t('SelectOptions.PaymentMethodOptions.accountTransfer') },
          { value: 'Reversal', label: t('SelectOptions.PaymentMethodOptions.reversal') },
          { value: 'Virtual Account Deposit', label: t('SelectOptions.PaymentMethodOptions.virtualAccountDeposit') },
          { value: 'Virtual Account Funding', label: t('SelectOptions.PaymentMethodOptions.virtualAccountFunding') },
          { value: 'Virtual Account Withdraw', label: t('SelectOptions.PaymentMethodOptions.virtualAccountWithdraw') },
          { value: 'Virtual Account Wire', label: t('SelectOptions.PaymentMethodOptions.virtualAccountWire') },
        ];

  const frequencyOptions = [
    {
      label: t('SelectOptions.FrequencyOptions.all'),
      value: '',
    },
    { label: t('SelectOptions.FrequencyOptions.daily'), value: 'daily' },
    { label: t('SelectOptions.FrequencyOptions.weekly'), value: 'weekly' },
    { label: t('SelectOptions.FrequencyOptions.monthly'), value: 'monthly' },
  ];

  const accountTransactionReportTypeOptions = [
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.all'),
      value: '',
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.approvalActivity', {
        defaultValue: 'Approval Activity',
      }),
      value: 'ApprovalActivityReport',
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.accountTransactionHistory', {
        defaultValue: 'Account Transaction History',
      }),
      value: 'AccountTransactionHistoryReport',
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.accountTransactionSummary'),
      value: 'AccountTransactionSummary',
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.endOfDayBalance'),
      value: 'EndOfDayBalance',
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.transactionsPerPaymentRail'),
      value: 'TransactionsPerPaymentRail',
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.transactionSuccessAndFailureAnalysisReport'),
      value: 'TransactionSuccessAndFailureAnalysisReport',
    },
    { label: t('SelectOptions.AccountTransactionReportTypeOptions.invoiceReport'), value: 'InvoiceReport' },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.interestAccountReport'),
      value: 'InterestReport',
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.glCodeTransactionSummary'),
      value: 'GLCodeTransactionSummary',
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.glCodeSummaryReport'),
      value: 'GLCodeSummaryReport',
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.transactionSummaryByCode'),
      value: 'TransactionSummaryByCode',
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.transactionCodeSummaryReport'),
      value: 'TransactionCodeSummaryReport',
    },
    {
      label: t('SelectOptions.AccountTransactionReportTypeOptions.paymentMethodChangeLogReport', {
        defaultValue: 'Payment Method Change Log',
      }),
      value: 'PaymentMethodChangeLogReport',
    },
  ];

  return (
    <div className={classes('oneTimeReportSearchFormContainer')}>
      <MultiSelect
        name="oneTimeReportPaymentMethods"
        value={currentPaymentMethods}
        placeholder={t('Placeholder.paymentMethod')}
        options={paymentMethodOptions}
        onChange={(e) => {
          const value = e.target.value;
          const cleanedPaymentMethods = value === '' ? [] : value.split(',').map((item) => item.trim());
          setCurrentPaymentMethods(cleanedPaymentMethods);
        }}
        onBlur={() => {}}
        sort={false}
      />
      <Select
        version="2"
        name="ReportType"
        value={currentReportType}
        placeholder={t('Placeholder.reportType')}
        options={accountTransactionReportTypeOptions}
        onBlur={() => {}}
        onChange={(e) => setCurrentReportType(e.target.value)}
        sort={false}
      />
      <Select
        version="2"
        name="Frequency"
        value={currentFrequency}
        placeholder={t('Placeholder.frequency')}
        options={frequencyOptions}
        onBlur={() => {}}
        onChange={(e) => setCurrentFrequency(e.target.value)}
      />
      <TextField
        key="TransactionSearchKeyword"
        name="TransactionSearchKeyword"
        placeholder={t('Placeholder.keyword')}
        value={currentSearchTerm}
        onBlur={() => {}}
        onChange={(e) => {
          setCurrentSearchTerm(e.target.value);
        }}
        variant="outlineShadow"
      />
      <Button
        className={styles.searchButton}
        id="search-transaction-history"
        colorScheme="primary"
        size="small"
        onClick={() => {
          onSearch({
            paymentMethods: currentPaymentMethods,
            reportType: currentReportType,
            frequency: currentFrequency,
            searchTerm: currentSearchTerm,
          });
        }}
      >
        {t('Placeholder.search')}
      </Button>
    </div>
  );
};

export default SubscriptionListSearchForm;
